import { useState, FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Settings } from 'lucide-react'

interface EditProps {
  user: {
    id: number
    timezone: string
  }
  errors?: string[]
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
    timezone?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

// Common timezones list
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time - Indiana' },
  { value: 'America/Detroit', label: 'Eastern Time - Michigan' },
  { value: 'America/New_York', label: 'Eastern Time - New York' },
  { value: 'America/Los_Angeles', label: 'Pacific Time - California' },
  { value: 'America/Denver', label: 'Mountain Time - Colorado' },
  { value: 'America/Chicago', label: 'Central Time - Illinois' },
  { value: 'America/Toronto', label: 'Eastern Time - Toronto' },
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
]

// Remove duplicates
const UNIQUE_TIMEZONES = Array.from(
  new Map(TIMEZONES.map(tz => [tz.value, tz])).values()
).sort((a, b) => a.label.localeCompare(b.label))

export default function Edit({ user, errors, currentUser, gmailConnection }: EditProps) {
  const [timezone, setTimezone] = useState(user.timezone || 'America/New_York')

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.put('/user_settings', {
      user: {
        timezone: timezone
      }
    })
  }

  return (
    <>
      <Head title="User Settings" />
      <DashboardLayout currentUser={currentUser} gmailConnection={gmailConnection}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
            User Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your personal preferences
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Timezone
            </CardTitle>
            <CardDescription>
              Set your timezone to display timestamps in your local time. This affects how dates and times are shown throughout the application.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors && errors.length > 0 && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <ul className="list-disc list-inside space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="timezone">
                  Timezone
                </Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger id="timezone" className="max-w-md">
                    <SelectValue placeholder="Select a timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIQUE_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  All timestamps will be displayed in your selected timezone.
                </p>
              </div>

              <div className="flex gap-3">
                <Button type="submit">
                  Save Settings
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => router.visit('/')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    </>
  )
}
